import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { getVOTD, votdImageUrl } from "../api";

const FALLBACK = {
  verse_text: "Your word is a lamp to my feet and a light to my path.",
  verse_ref: "Psalm 119:105",
  translation: "WEB",
  slug: null,
  has_image: false,
  occasion: null,
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function VerseOfDayCard() {
  const [votd, setVotd] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const d = await getVOTD();
          if (d && d.verse_text) {
            if (active) setVotd(d);
            return;
          }
        } catch {
          /* retry */
        }
        await wait(800 * (attempt + 1));
      }
      // network kept failing — show a dependable fallback so the card never vanishes
      if (active) setVotd(FALLBACK);
    })();
    return () => { active = false; };
  }, []);

  // API.Bible FUMS: report each view of licensed (NKJV/NLT) scripture
  useEffect(() => {
    if (votd?.fums_token && window.fums) {
      try { window.fums("trackView", votd.fums_token); } catch { /* noop */ }
    }
  }, [votd]);

  if (!votd || !votd.verse_text) return null;

  return (
    <button
      onClick={() => navigate("/verse")}
      data-testid="votd-card"
      className="group relative block h-60 w-full overflow-hidden rounded-3xl border border-border bg-[#2A1E13] text-left"
    >
      {votd.has_image && votd.slug && (
        <img
          src={votdImageUrl(votd.slug)}
          alt=""
          onLoad={() => setImgLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
      <div className="relative flex h-full flex-col justify-end p-6 text-white">
        <span className="font-ui mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/90">
          <Sparkles className="h-3.5 w-3.5" /> Verse of the Day{votd.occasion ? ` · ${votd.occasion}` : ""}
        </span>
        <p className="font-serif-display line-clamp-3 text-xl leading-snug drop-shadow-lg md:text-2xl">
          “{votd.verse_text}”
        </p>
        <p className="font-ui mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-white/90">
          {votd.verse_ref}{votd.translation ? ` · ${votd.translation}` : ""}
        </p>
        {votd.copyright && (
          <p data-testid="votd-copyright" className="font-ui mt-1 text-[10px] leading-tight text-white/55">
            {votd.copyright}
          </p>
        )}
      </div>
    </button>
  );
}
